import React, { useState } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const { showBanner, consent, acceptAll, acceptEssential, savePreferences, setShowBanner } = useCookieConsent();
  const { language } = useLanguage();
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(consent);

  if (!showBanner) return null;

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowSettings(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center pointer-events-none">
      <div className="fixed inset-0 bg-black/50 pointer-events-auto" onClick={() => {}} />
      
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full pointer-events-auto animate-slide-up">
        {!showSettings ? (
          // Main Banner
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Cookie className="w-8 h-8 text-cyan-600" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                </h2>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              {language === 'de' 
                ? 'Wir verwenden Cookies, um Ihr Erlebnis zu verbessern und unseren Service zu optimieren. Einige Cookies sind notwendig, andere helfen uns, unsere Website zu analysieren und zu verbessern.'
                : 'We use cookies to improve your experience and optimize our service. Some cookies are necessary, others help us analyze and improve our website.'}
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-5 h-5 rounded bg-cyan-600 flex items-center justify-center mt-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {language === 'de' ? 'Essenzielle Cookies' : 'Essential Cookies'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {language === 'de' 
                      ? 'Notwendig für die Funktion der Website' 
                      : 'Necessary for website functionality'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptAll}
                className="flex-1 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
              >
                {language === 'de' ? 'Alle akzeptieren' : 'Accept All'}
              </button>
              <button
                onClick={acceptEssential}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                {language === 'de' ? 'Nur notwendige' : 'Only Essential'}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center space-x-2 bg-white border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span>{language === 'de' ? 'Anpassen' : 'Customize'}</span>
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              {language === 'de' ? 'Mehr Informationen in unserer' : 'More information in our'}{' '}
              <Link to="/datenschutz" className="text-cyan-600 hover:text-cyan-800 underline">
                {language === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
              </Link>
            </p>
          </div>
        ) : (
          // Settings Panel
          <div className="p-6">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {language === 'de' ? 'Cookie-Einstellungen anpassen' : 'Customize Cookie Settings'}
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Essential */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-900">
                    {language === 'de' ? 'Essenzielle Cookies' : 'Essential Cookies'}
                  </label>
                  <div className="text-xs text-gray-600 font-semibold">
                    {language === 'de' ? 'Immer aktiv' : 'Always active'}
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  {language === 'de' 
                    ? 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.' 
                    : 'These cookies are required for basic website functionality.'}
                </p>
              </div>

              {/* Analytics */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-900">
                    {language === 'de' ? 'Analyse-Cookies' : 'Analytics Cookies'}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500"
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {language === 'de' 
                    ? 'Helfen uns zu verstehen, wie Besucher mit der Website interagieren (z.B. Google Analytics).' 
                    : 'Help us understand how visitors interact with the website (e.g. Google Analytics).'}
                </p>
              </div>

              {/* Marketing */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-900">
                    {language === 'de' ? 'Marketing-Cookies' : 'Marketing Cookies'}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500"
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {language === 'de' 
                    ? 'Werden verwendet, um Besuchern relevante Werbung anzuzeigen (z.B. Facebook Pixel, Google Ads).' 
                    : 'Used to show visitors relevant advertising (e.g. Facebook Pixel, Google Ads).'}
                </p>
              </div>

              {/* Preferences */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-900">
                    {language === 'de' ? 'Präferenz-Cookies' : 'Preference Cookies'}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.preferences}
                    onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                    className="w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500"
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {language === 'de' 
                    ? 'Speichern Ihre Einstellungen wie Sprache und Region.' 
                    : 'Store your preferences like language and region.'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
              >
                {language === 'de' ? 'Einstellungen speichern' : 'Save Preferences'}
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
